import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import EmailPage from "./pages/EmailPage";
import AdminPage from "./pages/AdminPage";
import DeveloperSettingsPage from "./pages/DeveloperSettingsPage";
import SupportDashboardPage from "./pages/SupportDashboardPage";
import HelpPage from "./pages/HelpPage";
import SentinelMailPage from "./pages/SentinelMailPage";
import LandingPage from "./pages/LandingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import ReferralDashboardPage from "./pages/ReferralDashboardPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RoadmapPage from "./pages/RoadmapPage";
import PricingPage from "./pages/PricingPage";
import WallPage from "./pages/WallPage";
import WallViewPage from "./pages/WallViewPage";
import WallInfoPage from "./pages/WallInfoPage";
import WallDemoPage from "./pages/WallDemoPage";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check and handle cache version
const checkCacheVersion = async () => {
  try {
    const response = await axios.get(`${BACKEND_URL}/api/admin/cache-version`);
    const serverVersion = response.data.version;
    const localVersion = parseInt(localStorage.getItem('cacheVersion') || '0', 10);
    
    if (serverVersion > localVersion) {
      console.log(`Cache version mismatch: local=${localVersion}, server=${serverVersion}. Clearing caches...`);
      
      // Clear localStorage (except auth token and user preferences)
      const token = localStorage.getItem('token');
      const preserveKeys = ['token', 'cacheVersion', 'notifications_enabled', 'notification_sound_enabled', 'theme', 'colorPreset', 'primaryColor', 'pwa_dismissed', 'pwa_installed'];
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (!preserveKeys.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear Service Worker caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('Service Worker caches cleared');
      }
      
      // Update local cache version
      localStorage.setItem('cacheVersion', serverVersion.toString());
      if (token) localStorage.setItem('token', token);
      
      // Reload to get fresh content
      window.location.reload();
    }
  } catch (error) {
    console.log('Cache version check skipped:', error.message);
  }
};

// Register Service Worker
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered:', registration.scope);
        })
        .catch((error) => {
          console.log('SW registration failed:', error);
        });
    });
  }
};

// Protected Route Component - also loads user preferences
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { loadUserPreferences } = useTheme();
  
  // Load user color preferences when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadUserPreferences();
    }
  }, [isAuthenticated, loadUserPreferences]);
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#16162a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D4AFF]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#16162a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D4AFF]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/inbox" replace />;
  }
  
  return children;
};

// Support Route (admin or support role)
const SupportRoute = ({ children }) => {
  const { user, isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#16162a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D4AFF]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin && user?.role !== 'support') return <Navigate to="/inbox" replace />;
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#16162a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D4AFF]"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/inbox" replace />;
  }
  
  return children;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } 
      />
      
      {/* Password Reset Routes (Public) */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/roadmap" element={<RoadmapPage />} />
      <Route path="/wall-info" element={<WallInfoPage />} />
      <Route path="/wall-demo" element={<WallDemoPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      
      {/* Payment Routes */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      
      {/* Protected Routes */}
      <Route 
        path="/inbox" 
        element={
          <ProtectedRoute>
            <EmailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/drafts" 
        element={
          <ProtectedRoute>
            <EmailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sent" 
        element={
          <ProtectedRoute>
            <EmailPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Account Settings */}
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <AccountSettingsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Referral Dashboard */}
      <Route 
        path="/referrals" 
        element={
          <ProtectedRoute>
            <ReferralDashboardPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        } 
      />
      
      {/* Developer Settings Route (Admin Only) */}
      <Route 
        path="/developer" 
        element={
          <AdminRoute>
            <DeveloperSettingsPage />
          </AdminRoute>
        } 
      />
      
      {/* Support Dashboard */}
      <Route 
        path="/support-dashboard" 
        element={
          <SupportRoute>
            <SupportDashboardPage />
          </SupportRoute>
        } 
      />
      
      {/* Sentinel Mail */}
      <Route 
        path="/sentinel" 
        element={
          <ProtectedRoute>
            <SentinelMailPage />
          </ProtectedRoute>
        } 
      />

      {/* Wall Routes */}
      <Route 
        path="/wall" 
        element={
          <ProtectedRoute>
            <WallPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/wall/setup" 
        element={
          <ProtectedRoute>
            <WallPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/wall/upgrade" 
        element={
          <ProtectedRoute>
            <WallPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/wall/:slug" 
        element={
          <ProtectedRoute>
            <WallViewPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Default Redirects */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// Themed Toaster component
const ThemedToaster = () => {
  const { isDarkMode } = useTheme();
  return <Toaster position="bottom-right" theme={isDarkMode ? "dark" : "light"} />;
};

function App() {
  useEffect(() => {
    registerServiceWorker();
    checkCacheVersion();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <NotificationProvider>
              <AppRoutes />
              <PWAInstallPrompt />
              <ImpersonationBanner />
              <ThemedToaster />
            </NotificationProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
